import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useDeleteFilteredSyncedData } from '@/hooks/integrations/useDeleteFilteredSyncedData';
import { Badge, BadgeWithButton } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { useUpdateCrmProperty } from '@/lib/crm/hooks/useUpdateCrmProperty';
import { useCrmObject } from '@/lib/crm/store';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';

import type { DntFieldKind } from './dntValidation';
import {
  extractDomainFromEmail,
  MAX_ENTRIES,
  normalizeEntry,
  validateEntry,
} from './dntValidation';

/** Minimal account shape consumed by AccountDntEditor — provider-agnostic.
 *  Works with both GmailStatusAccount (gmail_entity_id) and
 *  OutlookStatusAccount (outlook_mail_entity_id). */
export interface DntAccount {
  email: string | null;
}

export interface AccountDntEditorProps {
  orgId: string;
  account: DntAccount;
  /** The integration entity ID (e.g. gmail_entity_id or outlook_mail_entity_id). */
  entityId: string;
  userId: number;
  canModify: boolean;
}

const ENTITY_TYPE = 'ahoy_external_integration' as const;
const PROP_EMAILS = 'ahoy_filtered_emails' as const;
const PROP_DOMAINS = 'ahoy_filtered_domains' as const;

const INPUT_CLASS =
  'w-full px-3 py-2 text-sm border border-border-secondary rounded-lg bg-bg-primary text-primary focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-v2-primary)] disabled:opacity-50 disabled:cursor-not-allowed';

interface DntEntryBadgeProps {
  entry: string;
  canRemove: boolean;
  onRemove: (entry: string) => void;
}

function DntEntryBadge({ entry, canRemove, onRemove }: DntEntryBadgeProps) {
  const handleClick = useCallback(() => onRemove(entry), [onRemove, entry]);
  if (!canRemove) {
    return (
      <Badge type='pill-color' size='sm' color='gray'>
        {entry}
      </Badge>
    );
  }
  return (
    <BadgeWithButton
      type='pill-color'
      size='sm'
      color='gray'
      buttonLabel={`Remove ${entry}`}
      onButtonClick={handleClick}
    >
      {entry}
    </BadgeWithButton>
  );
}

interface AccountDntEditorBodyProps {
  orgId: string;
  account: DntAccount;
  entityId: string;
  userId: number;
  canModify: boolean;
  integrationObject: CrmObject;
}

function AccountDntEditorBody({
  orgId,
  account,
  entityId,
  userId,
  canModify,
  integrationObject,
}: AccountDntEditorBodyProps) {
  const savedEmails = useMemo<string[]>(() => {
    const raw = integrationObject.properties?.[PROP_EMAILS]?.value;
    return Array.isArray(raw)
      ? raw.filter((v): v is string => typeof v === 'string')
      : [];
  }, [integrationObject]);

  const savedDomains = useMemo<string[]>(() => {
    const raw = integrationObject.properties?.[PROP_DOMAINS]?.value;
    return Array.isArray(raw)
      ? raw.filter((v): v is string => typeof v === 'string')
      : [];
  }, [integrationObject]);

  const [emailsDraft, setEmailsDraft] = useState<string[]>(() => savedEmails);
  const [emailInput, setEmailInput] = useState('');
  const [emailValidationError, setEmailValidationError] = useState<
    string | null
  >(null);

  const [domainsDraft, setDomainsDraft] = useState<string[]>(
    () => savedDomains
  );
  const [domainInput, setDomainInput] = useState('');
  const [domainValidationError, setDomainValidationError] = useState<
    string | null
  >(null);

  const updateProperty = useUpdateCrmProperty(parseInt(orgId), userId);

  const [savingProperty, setSavingProperty] = useState<string | null>(null);
  const [emailServerError, setEmailServerError] = useState<string | null>(null);
  const [domainServerError, setDomainServerError] = useState<string | null>(
    null
  );

  const [pendingDeletionEntry, setPendingDeletionEntry] = useState<{
    entry: string;
    kind: DntFieldKind;
  } | null>(null);

  const { mutate: deleteFilteredDataMutate, isPending: isDeletionPending } =
    useDeleteFilteredSyncedData();

  // Persist a list to the integration entity immediately (save-on-edit). On a
  // server rejection, roll the optimistic draft change back to its prior state
  // so a rejected write never leaves an unsaved entry on screen, while keeping
  // previously-saved values intact.
  const persist = useCallback(
    (
      propertyName: string,
      nextList: string[],
      previousList: string[],
      setDraft: (next: string[]) => void,
      setServerError: (message: string | null) => void
    ) => {
      setSavingProperty(propertyName);
      setServerError(null);
      updateProperty.mutate(
        { object: integrationObject, propertyName, value: nextList },
        {
          onError: (err: unknown) => {
            setDraft(previousList);
            setPendingDeletionEntry(null);
            setServerError("We couldn't save your changes. Try again.");
            logger.error(
              'DNT save failed',
              err instanceof Error ? err : new Error(String(err)),
              {
                component: 'integrations',
                property: propertyName,
                entityId,
                count: nextList.length,
                errorType:
                  err instanceof Error ? err.constructor.name : 'Unknown',
              }
            );
          },
          onSettled: () => setSavingProperty(null),
        }
      );
    },
    [updateProperty, entityId, integrationObject]
  );

  const addEntry = useCallback(
    (kind: DntFieldKind) => {
      const input = kind === 'email' ? emailInput : domainInput;
      const draft = kind === 'email' ? emailsDraft : domainsDraft;
      const setDraft = kind === 'email' ? setEmailsDraft : setDomainsDraft;
      const setInput = kind === 'email' ? setEmailInput : setDomainInput;
      const setValidationErr =
        kind === 'email' ? setEmailValidationError : setDomainValidationError;
      const setServerError =
        kind === 'email' ? setEmailServerError : setDomainServerError;
      const propertyName = kind === 'email' ? PROP_EMAILS : PROP_DOMAINS;

      if (draft.length >= MAX_ENTRIES) {
        setValidationErr(`Maximum ${MAX_ENTRIES} entries reached.`);
        return;
      }
      const result = validateEntry(input, kind);
      if (!result.isValid) {
        setValidationErr(result.error ?? 'Invalid entry');
        return;
      }
      const normalized = normalizeEntry(input);
      setInput('');
      setValidationErr(null);
      if (draft.includes(normalized)) {
        // Already present — nothing to persist.
        return;
      }
      const nextList = [...draft, normalized];
      setDraft(nextList);
      setPendingDeletionEntry({ entry: normalized, kind });
      persist(propertyName, nextList, draft, setDraft, setServerError);
    },
    [emailInput, domainInput, emailsDraft, domainsDraft, persist]
  );

  const handleAddEmail = useCallback(() => addEntry('email'), [addEntry]);
  const handleAddDomain = useCallback(() => addEntry('domain'), [addEntry]);

  const removeEntry = useCallback(
    (kind: DntFieldKind, entry: string) => {
      const draft = kind === 'email' ? emailsDraft : domainsDraft;
      const setDraft = kind === 'email' ? setEmailsDraft : setDomainsDraft;
      const setServerError =
        kind === 'email' ? setEmailServerError : setDomainServerError;
      const propertyName = kind === 'email' ? PROP_EMAILS : PROP_DOMAINS;

      const nextList = draft.filter(e => e !== entry);
      setDraft(nextList);
      persist(propertyName, nextList, draft, setDraft, setServerError);
    },
    [emailsDraft, domainsDraft, persist]
  );

  const handleRemoveEmail = useCallback(
    (entry: string) => removeEntry('email', entry),
    [removeEntry]
  );

  const handleRemoveDomain = useCallback(
    (entry: string) => removeEntry('domain', entry),
    [removeEntry]
  );

  const selfDomain = useMemo(
    () => extractDomainFromEmail(account.email ?? ''),
    [account.email]
  );
  const showSelfDomainWarning = useMemo(
    () => Boolean(selfDomain && domainsDraft.includes(selfDomain)),
    [domainsDraft, selfDomain]
  );

  const handleEmailInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmailInput(e.target.value);
    },
    []
  );

  const handleDomainInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDomainInput(e.target.value);
    },
    []
  );

  const handleEmailKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddEmail();
      }
    },
    [handleAddEmail]
  );

  const handleDomainKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddDomain();
      }
    },
    [handleAddDomain]
  );

  const deletionDescription = useMemo(() => {
    if (!pendingDeletionEntry) {
      return '';
    }
    const { entry, kind } = pendingDeletionEntry;
    const suffix = kind === 'email' ? 'from this address' : 'from this domain';
    return `You've filtered ${entry}. Would you also like to delete any emails and external contacts already synced ${suffix}?`;
  }, [pendingDeletionEntry]);

  const handleDeletionDialogOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setPendingDeletionEntry(null);
    }
  }, []);

  const handleDismissDeletion = useCallback(() => {
    setPendingDeletionEntry(null);
  }, []);

  const handleConfirmDeletion = useCallback(() => {
    if (!pendingDeletionEntry) {
      return;
    }
    const { entry, kind } = pendingDeletionEntry;
    setPendingDeletionEntry(null);

    deleteFilteredDataMutate(
      {
        orgId,
        integrationEntityId: entityId,
        emails: kind === 'email' ? [entry] : [],
        domains: kind === 'domain' ? [entry] : [],
      },
      {
        onSuccess: () => {
          toast.success('Deletion started', {
            description: `We're deleting synced data for ${entry}. This may take a few minutes.`,
          });
        },
        onError: () => {
          toast.error("Couldn't start deletion", {
            description: 'Please try again from the integration settings.',
          });
        },
      }
    );
  }, [pendingDeletionEntry, orgId, entityId, deleteFilteredDataMutate]);

  return (
    <div className='space-y-4'>
      {/* Filtered email addresses */}
      <section aria-labelledby='dnt-email-heading'>
        <p
          id='dnt-email-heading'
          className='text-sm font-medium text-primary mb-1'
        >
          Filtered email addresses
        </p>
        <p className='text-xs text-tertiary mb-2'>
          Emails from these addresses are excluded from Ahoy processing.
        </p>
        <ul role='list' className='flex flex-wrap gap-2 mb-2'>
          {emailsDraft.length === 0 ? (
            <li className='text-xs text-tertiary'>No entries.</li>
          ) : (
            emailsDraft.map(entry => (
              <li key={entry}>
                <DntEntryBadge
                  entry={entry}
                  canRemove={canModify}
                  onRemove={handleRemoveEmail}
                />
              </li>
            ))
          )}
        </ul>
        {canModify && (
          <div className='space-y-1'>
            <label
              htmlFor='dnt-email-input'
              className='text-xs font-medium text-secondary block'
            >
              Add email address
            </label>
            <div className='flex gap-2'>
              <input
                id='dnt-email-input'
                type='text'
                value={emailInput}
                onChange={handleEmailInputChange}
                onKeyDown={handleEmailKeyDown}
                aria-invalid={emailValidationError !== null}
                aria-describedby={
                  emailValidationError ? 'dnt-email-error' : undefined
                }
                placeholder='user@example.com'
                className={INPUT_CLASS}
                disabled={
                  savingProperty !== null || emailsDraft.length >= MAX_ENTRIES
                }
              />
              <Button
                color='secondary'
                size='sm'
                onClick={handleAddEmail}
                isDisabled={
                  savingProperty !== null || emailsDraft.length >= MAX_ENTRIES
                }
              >
                Add
              </Button>
            </div>
            {emailValidationError && (
              <p
                id='dnt-email-error'
                role='alert'
                className='text-xs text-error-600'
              >
                {emailValidationError}
              </p>
            )}
          </div>
        )}
        {emailServerError && (
          <p role='alert' className='text-xs text-error-600 mt-1'>
            {emailServerError}
          </p>
        )}
      </section>

      {/* Filtered domains */}
      <section aria-labelledby='dnt-domain-heading'>
        <p
          id='dnt-domain-heading'
          className='text-sm font-medium text-primary mb-1'
        >
          Filtered domains
        </p>
        <p className='text-xs text-tertiary mb-2'>
          Emails from these domains are excluded from Ahoy processing. Enter a
          bare domain (e.g. example.com).
        </p>
        <ul role='list' className='flex flex-wrap gap-2 mb-2'>
          {domainsDraft.length === 0 ? (
            <li className='text-xs text-tertiary'>No entries.</li>
          ) : (
            domainsDraft.map(entry => (
              <li key={entry}>
                <DntEntryBadge
                  entry={entry}
                  canRemove={canModify}
                  onRemove={handleRemoveDomain}
                />
              </li>
            ))
          )}
        </ul>
        {showSelfDomainWarning && (
          <p
            role='status'
            aria-live='polite'
            className='text-xs text-warning-700 mb-2'
          >
            Heads up: this domain matches your own email&apos;s domain. Adding
            it will exclude all messages from your own organization.
          </p>
        )}
        {canModify && (
          <div className='space-y-1'>
            <label
              htmlFor='dnt-domain-input'
              className='text-xs font-medium text-secondary block'
            >
              Add domain
            </label>
            <div className='flex gap-2'>
              <input
                id='dnt-domain-input'
                type='text'
                value={domainInput}
                onChange={handleDomainInputChange}
                onKeyDown={handleDomainKeyDown}
                aria-invalid={domainValidationError !== null}
                aria-describedby={
                  domainValidationError ? 'dnt-domain-error' : undefined
                }
                placeholder='example.com'
                className={INPUT_CLASS}
                disabled={
                  savingProperty !== null || domainsDraft.length >= MAX_ENTRIES
                }
              />
              <Button
                color='secondary'
                size='sm'
                onClick={handleAddDomain}
                isDisabled={
                  savingProperty !== null || domainsDraft.length >= MAX_ENTRIES
                }
              >
                Add
              </Button>
            </div>
            {domainValidationError && (
              <p
                id='dnt-domain-error'
                role='alert'
                className='text-xs text-error-600'
              >
                {domainValidationError}
              </p>
            )}
          </div>
        )}
        {domainServerError && (
          <p role='alert' className='text-xs text-error-600 mt-1'>
            {domainServerError}
          </p>
        )}
      </section>

      <ModalOverlay
        isOpen={pendingDeletionEntry !== null}
        onOpenChange={handleDeletionDialogOpenChange}
      >
        <Modal className='max-w-md'>
          <Dialog
            aria-label='Delete existing synced data'
            title='Delete existing synced data?'
            description={deletionDescription}
            footer={
              <div className='flex justify-end gap-3'>
                <Button
                  color='secondary'
                  size='sm'
                  onClick={handleDismissDeletion}
                >
                  No thanks
                </Button>
                <Button
                  color='primary-destructive'
                  size='sm'
                  onClick={handleConfirmDeletion}
                  isDisabled={isDeletionPending}
                >
                  Delete synced data
                </Button>
              </div>
            }
          >
            <p className='text-xs text-[var(--color-text-tertiary)]'>
              This will permanently delete synced data. Your CRM contacts and
              deals won&apos;t be affected.
            </p>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </div>
  );
}

export default function AccountDntEditor({
  orgId,
  account,
  entityId,
  userId,
  canModify,
}: AccountDntEditorProps) {
  const {
    object: integrationObject,
    isLoading,
    error,
  } = useCrmObject(parseInt(orgId), ENTITY_TYPE, entityId);

  useEffect(() => {
    if (error) {
      logger.error(
        'Failed to load DNT integration data',
        error instanceof Error ? error : new Error(String(error)),
        { entityId }
      );
    }
  }, [error, entityId]);

  if (isLoading) {
    return (
      <section>
        <LoadingIndicator size='sm' />
      </section>
    );
  }

  if (error) {
    return (
      <section>
        <p role='alert' className='text-xs text-error-600'>
          We couldn't load this data. Try again.
        </p>
      </section>
    );
  }

  if (!integrationObject) {
    return null;
  }

  return (
    <AccountDntEditorBody
      orgId={orgId}
      account={account}
      entityId={entityId}
      userId={userId}
      canModify={canModify}
      integrationObject={integrationObject}
    />
  );
}
